import type { ClinicResponse } from '@/api/hospital';

/**
 * True when the clinician has ≥2 clinics in the same hospital as the patient's
 * first shared clinic — the visibility gate for the move-clinic action.
 *
 * Lives here rather than beside `MovePatientClinicDialog` so that file exports
 * only its component: mixing a helper in trips `react-refresh/only-export-components`,
 * which CI runs at `--max-warnings 0`.
 */
export function canMovePatientBetweenClinics(args: {
  patientClinics: Array<{ id: number }> | undefined | null;
  clinicianClinics: ClinicResponse[];
  homeHospitalId?: number | null;
}): boolean {
  const { patientClinics, clinicianClinics, homeHospitalId } = args;
  if (!patientClinics?.length || clinicianClinics.length < 2) return false;

  const clinicianById = new Map(clinicianClinics.map((c) => [c.id, c]));
  const source = patientClinics.map((pc) => clinicianById.get(pc.id)).find(Boolean) ?? null;
  if (!source) return false;

  const hospitalId = source.hospital_id ?? homeHospitalId ?? undefined;
  if (hospitalId == null) return false;

  const sameHospital = clinicianClinics.filter(
    (c) => (c.hospital_id ?? homeHospitalId) === hospitalId,
  );
  return sameHospital.length > 1;
}
